// LeadCard.jsx
import * as React from 'react';
import {
  Card, CardContent, CardActionArea, IconButton, Box, Stack, Typography, Chip, Avatar, Tooltip, Divider
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import RoofingIcon from '@mui/icons-material/Architecture';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import ForestIcon from '@mui/icons-material/Forest';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import BuildIcon from '@mui/icons-material/Build';
import PlaceIcon from '@mui/icons-material/Place';

const inferServiceType = (post) =>
  post?.serviceType ||
  post?.aiSummary?.serviceType ||
  post?.ai?.serviceType ||
  (Array.isArray(post?.tags) && post.tags.find(t => /^SVC:/.test(t.id))?.label) ||
  'General';

const serviceIcon = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('tree')) return <ForestIcon fontSize="small" />;
  if (s.includes('lawn') || s.includes('landscap')) return <LocalFloristIcon fontSize="small" />;
  if (s.includes('roof')) return <RoofingIcon fontSize="small" />;
  if (s.includes('hvac') || s.includes('ac')) return <AcUnitIcon fontSize="small" />;
  if (s.includes('plumb')) return <PlumbingIcon fontSize="small" />;
  return <BuildIcon fontSize="small" />;
};

const serviceColors = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('tree')) return { bg: '#dcfce7', fg: '#065f46' };
  if (s.includes('lawn') || s.includes('landscap')) return { bg: '#d1fae5', fg: '#065f46' };
  if (s.includes('roof')) return { bg: '#fee2e2', fg: '#991b1b' };
  if (s.includes('hvac') || s.includes('ac')) return { bg: '#dbeafe', fg: '#1e3a8a' };
  if (s.includes('plumb')) return { bg: '#e0e7ff', fg: '#3730a3' };
  return { bg: '#f3f4f6', fg: '#111827' };
};

export function LeadCard({
  post,
  isSaved,
  onToggleHeart,   // (e) => toggleHeart(post, e)
  onOpen,          // () => openDetails(post)
  cleanFbNoise,
  getPostedAtMs,
  isNewLead
}) {
  const key = `${post?.postId || post?.id || post?._path || Math.random()}`;
  const avatarUrl = Array.isArray(post?.images) && post.images[0] ? post.images[0] : undefined;
  const author = post?.author || 'Unknown';
  const whenMs = getPostedAtMs(post);
  const when = whenMs ? new Date(whenMs).toLocaleString() : '';
  const service = inferServiceType(post);
  const svcColors = serviceColors(service);
  const nbhd = post?.neighborhood || post?.headerNeighborhood || null;
  const preview = cleanFbNoise(post?.text || '');

  return (
    <Card
      key={key}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 10px 24px rgba(2,6,23,0.08)',
        transition: 'transform .15s ease, box-shadow .15s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 18px 36px rgba(2,6,23,0.12)' },
        border: '1px solid #eef2ff',
        background: '#fff',
      }}
    >
      {/* NEW dot (optional, keeps your “new within 24h” cue) */}
      {isNewLead(post) && (
        <Box sx={{ position: 'absolute', left: 8, top: 8, width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
      )}

      <CardActionArea onClick={onOpen}>
        <CardContent sx={{ p: 1.25 }}>
          {/* Top row: Avatar + author/timestamp, heart on right */}
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
            <Avatar
              src={avatarUrl}
              alt={author}
              sx={{ width: 44, height: 44, border: '2px solid #fff', boxShadow: '0 2px 6px rgba(0,0,0,0.12)', bgcolor: '#0ea5e9', fontWeight: 700 }}
            >
              {author.slice(0, 1).toUpperCase()}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                sx={{ lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={author}
              >
                {author}
              </Typography>

              {!!when && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {when}
                </Typography>
              )}
            </Box>

            <IconButton aria-label="save lead" onClick={onToggleHeart} sx={{ mt: -0.5 }}>
              {isSaved ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
            </IconButton>
          </Stack>

          {/* Service + Neighborhood chips row */}
          <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Tooltip title="AI-suggested service type">
              <Chip
                icon={serviceIcon(service)}
                label={service}
                size="small"
                sx={{ bgcolor: svcColors.bg, color: svcColors.fg, fontWeight: 700, borderRadius: 9999 }}
              />
            </Tooltip>
            {!!nbhd && (
              <Chip
                size="small"
                icon={<PlaceIcon fontSize="small" />}
                label={nbhd}
                sx={{ bgcolor: '#f1f5f9', color: '#0f172a', fontWeight: 700, borderRadius: 9999 }}
              />
            )}
          </Stack>

          {/* Excerpt */}
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              color: '#0f172a',
            }}
            title={preview}
          >
            {preview || 'No content found'}
          </Typography>

          <Divider sx={{ my: 1 }} />

          {/* Bottom meta */}
          <Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip size="small" label={post.platform || 'nextdoor'} sx={{ bgcolor: '#DBEAFE', fontWeight: 700 }} />
            {post.market && <Chip size="small" label={post.market} sx={{ bgcolor: '#FDE68A', fontWeight: 700 }} />}
            {post.subtype && <Chip size="small" label={post.subtype} sx={{ bgcolor: '#E9D5FF', fontWeight: 700 }} />}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}